import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

/**
 * CaseReaffirmations.jsx
 *
 * Endpoints used:
 * - GET  /api/case/:caseId/reaffirmations/                 (optional; if not implemented, we fallback)
 * - POST /api/case/:caseId/reaffirmations/initialize/
 * - POST /api/case/:caseId/reaffirmations/send-outstanding/
 * - POST /api/reaffirmations/:itemId/agreement/            (multipart {file})
 * - POST /api/reaffirmations/:itemId/extract-signature-pages/  (json {pages:[...]})
 * - POST /api/reaffirmations/:itemId/review/               (json {action, reason})
 * - POST /api/reaffirmations/:itemId/client-signed/        (multipart {file})
 *
 * Assumes JWT token in localStorage under "token" (adjust as needed).
 */
const API_URL = process.env.REACT_APP_API_URL;
function apiFetch(url, options = {}) {
  const token = localStorage.getItem("accessToken");
  const headers = new Headers(options.headers || {});
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(url, { ...options, headers });
}

function statusPill(status) {
  const map = {
    draft: "bg-gray-100 text-gray-800 ring-1 ring-gray-200",
    sent: "bg-blue-50 text-blue-800 ring-1 ring-blue-200",
    client_uploaded: "bg-yellow-50 text-yellow-900 ring-1 ring-yellow-200",
    accepted: "bg-green-50 text-green-800 ring-1 ring-green-200",
    rejected: "bg-red-50 text-red-800 ring-1 ring-red-200",
  };
  return map[status] || "bg-gray-100 text-gray-800 ring-1 ring-gray-200";
}
function formatDate(val) {
  if (!val) return null;
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function parsePagesInput(input) {
  // Accepts "7-8, 10, 12-13" -> [7,8,10,12,13]
  if (!input) return [];
  const parts = input
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  const out = new Set();
  for (const p of parts) {
    if (p.includes("-")) {
      const [aRaw, bRaw] = p.split("-").map((x) => x.trim());
      const a = parseInt(aRaw, 10);
      const b = parseInt(bRaw, 10);
      if (!Number.isFinite(a) || !Number.isFinite(b) || a < 1 || b < 1) continue;
      const start = Math.min(a, b);
      const end = Math.max(a, b);
      for (let i = start; i <= end; i++) out.add(i);
    } else {
      const n = parseInt(p, 10);
      if (Number.isFinite(n) && n >= 1) out.add(n);
    }
  }
  return Array.from(out).sort((x, y) => x - y);
}

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/70"
        onClick={onClose}
      />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-xl rounded-2xl bg-white ring-1 ring-gray-200 shadow-xl">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-900"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          <div className="px-5 py-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Spinner({ className = "" }) {
  return (
    <span
      className={`inline-block h-4 w-4 rounded-full border-2 border-gray-300 border-t-gray-700 animate-spin ${className}`}
      aria-label="Loading"
    />
  );
}

export default function CaseReaffirmations() {
  const { caseId } = useParams();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyMsg, setBusyMsg] = useState("");
  const [toast, setToast] = useState(null);

  const [extractOpen, setExtractOpen] = useState(false);
  const [extractItem, setExtractItem] = useState(null);
  const [pageText, setPageText] = useState("");

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [items]);
  const displayedCaseNumber = useMemo(() => {
    const itemWithCaseNumber = items.find((item) => String(item?.case_number || "").trim());
    return itemWithCaseNumber?.case_number || caseId;
  }, [items, caseId]);
    async function deleteAttachment(attId) {
    if (!attId) return;
    const ok = window.confirm("Delete this attachment? This cannot be undone.");
    if (!ok) return;

    setBusy(true);
    setBusyMsg("Deleting attachment…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/reaffirmations/attachments/${attId}/`, {
        method: "DELETE",
      });
      if (!res.ok) {
        let data = null;
        try {
          data = await res.json();
        } catch (e) {
          // ignore
        }
        throw new Error((data && data.detail) || "Failed to delete attachment.");
      }
      setToast({ type: "success", msg: "Attachment deleted." });
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Delete failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }
        const deadline = useMemo(() => {
  // serializer provides reaffirmation_deadline on each item
  const first = items?.find?.((i) => i?.reaffirmation_deadline) || null;
  return first ? first.reaffirmation_deadline : null;
}, [items]);
  async function loadItems() {
    setBusy(true);
    setBusyMsg("Loading reaffirmations…");
    setToast(null);
    try {
      // Prefer a list endpoint if you have it; fallback to initialize response.
      const res = await apiFetch(`${API_URL}/case/${caseId}/reaffirmations/`, { method: "GET" });
      if (res.ok) {
        const data = await res.json();
        setItems(data.items || data || []);
        return;
      }
      // Fallback: do NOT auto-initialize silently; but if list isn't implemented,
      // we use initialize to at least show something and keep your flow moving.
      const init = await apiFetch(`${API_URL}/case/${caseId}/reaffirmations/initialize/`, { method: "POST" });
      const initData = await init.json();
      if (!init.ok) throw new Error(initData.detail || "Failed to initialize reaffirmations.");
      setItems(initData.items || []);
      setToast({ type: "info", msg: `Loaded reaffirmations (created ${initData.created || 0}).` });
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Failed to load reaffirmations." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  async function initializeOnDemand() {
    setBusy(true);
    setBusyMsg("Initializing reaffirmations…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/case/${caseId}/reaffirmations/initialize/`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to initialize reaffirmations.");
      setItems(data.items || []);
      setToast({ type: "success", msg: `Initialized reaffirmations (created ${data.created || 0}).` });
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Initialize failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  async function sendOutstanding() {
    setBusy(true);
    setBusyMsg("Sending reaffirmation email(s)…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/case/${caseId}/reaffirmations/send-outstanding/`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to send outstanding reaffirmations.");
      setToast({ type: "success", msg: `Sent ${data.sent_total || 0} reaffirmation(s).` });
      // refresh
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Send failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  function getAttachment(item, kind) {
    return (item.attachments || []).find((a) => a.kind === kind) || null;
  }

  function attachmentName(att) {
    return att?.original_filename || (att?.file ? att.file.split("/").slice(-1)[0] : "file");
  }

  function safeDownloadPart(value) {
    return String(value || '')
      .replace(/[\\/:*?"<>|]+/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/^[ .-]+|[ .-]+$/g, '');
  }

  function finalReaffirmationDownloadName(item, att) {
    const names = [item?.debtor1_name, item?.debtor2_name]
      .filter(Boolean)
      .map(safeDownloadPart)
      .filter(Boolean)
      .join(' & ');
    const collateral = collateralList(item)
      .map(safeDownloadPart)
      .filter(Boolean)
      .join(' & ');
    const original = safeDownloadPart(attachmentName(att)) || 'Final Reaffirmation.pdf';
    const originalPdf = original.toLowerCase().endsWith('.pdf') ? original : `${original}.pdf`;
    return [
      safeDownloadPart(item?.case_number || displayedCaseNumber),
      names,
      collateral,
      originalPdf,
    ]
      .filter(Boolean)
      .join(' - ');
  }

  async function downloadFinalReaffirmation(item, att) {
    setBusy(true);
    setBusyMsg('Downloading final reaffirmation…');
    setToast(null);
    try {
      const response = await apiFetch(
        `${API_URL}/api/reaffirmations/attachments/${att.id}/final-download/`,
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Final reaffirmation download failed.');
      }
      const disposition = response.headers.get('Content-Disposition') || '';
      const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const plainMatch = disposition.match(/filename="?([^";]+)"?/i);
      let filename = finalReaffirmationDownloadName(item, att);
      try {
        if (utf8Match?.[1]) filename = decodeURIComponent(utf8Match[1]);
        else if (plainMatch?.[1]) filename = plainMatch[1];
      } catch (_) {
        // Keep the case/debtor fallback filename if the header is malformed.
      }
      const objectUrl = window.URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setToast({ type: 'error', msg: error.message || 'Download failed.' });
    } finally {
      setBusyMsg('');
      setBusy(false);
    }
  }

  function debtorNameFromItem(item, which) {
    // which: 'debtor1' | 'debtor2'
    const key = which === 'debtor1' ? 'debtor1_name' : 'debtor2_name';
    const altKey = which === 'debtor1' ? 'primary_debtor_name' : 'secondary_debtor_name';
    return (
      item?.[key] ||
      item?.[altKey] ||
      item?.debtors?.find?.((d) => d.is_filing !== false && (which === 'debtor1' ? d.is_primary : !d.is_primary))?.full_name ||
      item?.debtors?.find?.((d) => d.is_filing !== false && (which === 'debtor1' ? d.is_primary : !d.is_primary))?.name ||
      null
    );
  }

  function whoOwesLabel(item) {
    // Prefer a backend-provided display label if present
    return (
      item?.who_owes_display ||
      item?.creditor_who_owes_display ||
      item?.who_owes ||
      item?.creditor_who_owes ||
      null
    );
  }

  function whoOwesNames(item) {
    const who = (item?.who_owes || item?.creditor_who_owes || '').toString();
    const d1 = debtorNameFromItem(item, 'debtor1');
    const d2 = debtorNameFromItem(item, 'debtor2');

    if (who === 'debtor1') return d1 || 'Debtor 1';
    if (who === 'debtor2') return d2 || 'Debtor 2';
    if (who === 'joint') return [d1 || 'Debtor 1', d2 || 'Debtor 2'].join(' & ');
    if (who === 'debtor1_third') return (d1 || 'Debtor 1') + ' (and third party)';
    if (who === 'debtor2_third') return (d2 || 'Debtor 2') + ' (and third party)';
    if (who === 'authorized') return (d1 || 'Debtor 1') + ' (authorized user)';

    // If backend didn’t send who_owes, fall back to anything reasonable
    return d1 || d2 || whoOwesLabel(item) || '—';
  }

  function money(val) {
    if (val === null || val === undefined || val === '') return '—';
    const num = typeof val === 'number' ? val : Number(val);
    if (!Number.isFinite(num)) return String(val);
    return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  }

  function formatDateTime(val) {
    if (!val) return null;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function collateralList(item) {
    // Accept several possible shapes from backend
    const arr =
      item?.collateral_lines ||
      item?.collateral ||
      item?.collateral_descriptions ||
      item?.collateral_description ||
      null;

    if (!arr) return [];
    if (Array.isArray(arr)) return arr.filter(Boolean);
    return [String(arr)].filter(Boolean);
  }

  async function uploadAgreement(itemId, file) {
    setBusy(true);
    setBusyMsg("Uploading agreement…");
    setToast(null);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await apiFetch(`${API_URL}/reaffirmations/${itemId}/agreement/`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Agreement upload failed.");
      setToast({ type: "success", msg: "Agreement uploaded." });
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Upload failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  async function uploadFinalSigned(itemId, file) {
    setBusy(true);
    setBusyMsg("Uploading final signed agreement…");
    setToast(null);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await apiFetch(`${API_URL}/reaffirmations/${itemId}/final-signed/`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Final signed upload failed.");
      setToast({ type: "success", msg: "Final signed reaffirmation uploaded." });
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Upload failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  async function uploadClientSigned(itemId, file) {
    setBusy(true);
    setBusyMsg("Uploading client-signed copy…");
    setToast(null);
    try {
      const fd = new FormData();
      fd.append("file", file);

      const res = await apiFetch(`${API_URL}/reaffirmations/${itemId}/client-signed/`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Client-signed upload failed.");
      setToast({ type: "success", msg: "Client-signed reaffirmation uploaded and ready for review." });
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Upload failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  function openExtractModal(item) {
    setExtractItem(item);
    setPageText("");
    setExtractOpen(true);
  }

  async function extractPages() {
    const pages = parsePagesInput(pageText);
    if (!extractItem) return;

    if (!pages.length) {
      setToast({ type: "error", msg: "Enter valid pages (e.g., 7-8, 10, 12)." });
      return;
    }

    setBusy(true);
    setBusyMsg("Extracting signature pages…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/reaffirmations/${extractItem.id}/extract-signature-pages/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pages }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Extraction failed.");
      setToast({ type: "success", msg: "Signature pages extracted." });
      setExtractOpen(false);
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Extraction failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  async function acceptItem(itemId) {
    setBusy(true);
    setBusyMsg("Accepting client upload…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/reaffirmations/${itemId}/review/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "accept" }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Accept failed.");
      setToast({ type: "success", msg: "Accepted." });
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Accept failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  function openRejectModal(item) {
    setRejectItem(item);
    setRejectReason("");
    setRejectOpen(true);
  }

  async function rejectItemSubmit() {
    if (!rejectItem) return;
    setBusy(true);
    setBusyMsg("Rejecting client upload…");
    setToast(null);
    try {
      const res = await apiFetch(`${API_URL}/reaffirmations/${rejectItem.id}/review/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "reject", reason: rejectReason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Reject failed.");
      setToast({ type: "success", msg: "Rejected and saved reason." });
      setRejectOpen(false);
      await loadItems();
    } catch (e) {
      setToast({ type: "error", msg: e.message || "Reject failed." });
    } finally {
      setBusyMsg("");
      setBusy(false);
    }
  }

  function hasAttachment(item, kind) {
    return (item.attachments || []).some((a) => a.kind === kind);
  }

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
  <div>
    <h1 className="text-2xl font-semibold text-gray-900">Reaffirmations</h1>
    <p className="text-sm text-gray-600 mt-1">
      Case: <span className="font-medium text-gray-900">{displayedCaseNumber}</span>
    </p>
    {deadline && (
  <p className="text-sm text-gray-600 mt-1">
    Reaffirmation deadline:{" "}
    <span className="font-semibold text-gray-900">{formatDate(deadline)}</span>
  </p>
)}
  </div>

  <div className="flex flex-wrap gap-2">
    <button
      onClick={initializeOnDemand}
      disabled={busy}
      className="px-4 py-2 rounded-lg bg-white text-gray-900 hover:bg-gray-50 ring-1 ring-gray-300 disabled:opacity-50"
    >
      {busy ? (
        <span className="inline-flex items-center gap-2"><Spinner className="border-t-gray-900" /> Working…</span>
      ) : (
        "Initialize"
      )}
    </button>
    <button
      onClick={sendOutstanding}
      disabled={busy}
      className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
    >
      {busy ? (
        <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Working…</span>
      ) : (
        "Send Outstanding"
      )}
    </button>
  </div>
</header>
      {busy && (
        <div className="mt-4 rounded-xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3 flex items-center gap-3">
          <Spinner />
          <div className="text-sm text-gray-800">
            {busyMsg || "Working…"}
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`mt-4 rounded-xl p-4 ring-1 ${
            toast.type === "error"
              ? "bg-red-50 ring-red-200 text-red-900"
              : toast.type === "success"
              ? "bg-green-50 ring-green-200 text-green-900"
              : "bg-blue-50 ring-blue-200 text-blue-900"
          }`}
        >
          {toast.msg}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {sortedItems.map((item) => (
          <div key={item.id} className="rounded-2xl bg-white ring-1 ring-gray-200 p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold text-gray-900">{item.creditor_name}</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${statusPill(item.status)}`}>
                    {item.status === "sent"
                      ? `sent${formatDateTime(item.sent_at) ? ` • ${formatDateTime(item.sent_at)}` : ""}`
                      : (item.status?.replaceAll("_", " ") || "unknown")}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1 space-y-1">
                  <div>
                    <span className="text-gray-700">Debt type:</span> {item.creditor_debt_type || "—"}{" "}
                    <span className="text-gray-300 mx-1">•</span>
                    <span className="text-gray-700">Intent:</span> {item.creditor_intent || "—"}
                  </div>

                  <div>
                    <span className="text-gray-700">Who owes:</span>{" "}
                    <span className="text-gray-900 font-medium">{whoOwesNames(item)}</span>
                    {whoOwesLabel(item) && (
                      <span className="text-gray-500">{` (${whoOwesLabel(item)})`}</span>
                    )}
                  </div>

                  <div>
                    <span className="text-gray-700">Balance:</span>{" "}
                    <span className="text-gray-900 font-medium">
                      {money(item.debt_balance ?? item.balance ?? item.creditor_balance ?? item.amount_owed)}
                    </span>
                  </div>

                  {collateralList(item).length > 0 && (
                    <div>
                      <span className="text-gray-700">Collateral:</span>
                      <ul className="mt-1 list-disc pl-5 text-gray-900">
                        {collateralList(item).map((c, idx) => (
                          <li key={idx} className="break-words">
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                {item.rejection_reason && (
                  <div className="mt-2 text-sm text-red-900 bg-red-50 ring-1 ring-red-200 rounded-lg p-3">
                    <div className="font-semibold">Rejection reason</div>
                    <div className="mt-1">{item.rejection_reason}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-white ring-1 ring-gray-200 p-4">
                <div className="text-sm font-semibold text-gray-900">Agreement</div>
                <div className="text-xs text-gray-600 mt-1">
                  {hasAttachment(item, "agreement") ? "Uploaded" : "Not uploaded"}
                </div>

                {(() => {
                  const att = getAttachment(item, "agreement");
                  if (!att || !att.file) return null;
                  const raw = att.file_url || att.file;
                  if (!raw) return null;
                  const href = raw.startsWith("http") ? raw : `${API_URL}${raw}`;
                  return (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <a
                        className="text-sm text-indigo-700 hover:text-indigo-900 underline break-all"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View / Download: {attachmentName(att)}
                      </a>
                      <button
                        type="button"
                        onClick={() => deleteAttachment(att.id)}
                        disabled={busy}
                        className="shrink-0 text-xs px-2 py-1 rounded-lg bg-white hover:bg-gray-50 text-red-700 ring-1 ring-red-200 disabled:opacity-50"
                        title="Delete attachment"
                      >
                        Delete
                      </button>
                    </div>
                  );
                })()}

                <label className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 cursor-pointer disabled:opacity-50">
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadAgreement(item.id, f);
                      e.target.value = "";
                    }}
                    disabled={busy}
                  />
                  {busy ? (
                    <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Uploading…</span>
                  ) : (
                    "Upload PDF"
                  )}
                </label>
              </div>

              <div className="rounded-xl bg-white ring-1 ring-gray-200 p-4">
                <div className="text-sm font-semibold text-gray-900">Signature Pages</div>
                <div className="text-xs text-gray-600 mt-1">
                  {hasAttachment(item, "signature_pages") ? "Extracted" : "Not extracted"}
                </div>

                {(() => {
                  const att = getAttachment(item, "signature_pages");
                  if (!att || !att.file) return null;
                  const raw = att.file_url || att.file;
                  if (!raw) return null;
                  const href = raw.startsWith("http") ? raw : `${API_URL}${raw}`;
                  return (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <a
                        className="text-sm text-indigo-700 hover:text-indigo-900 underline break-all"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View / Download: {attachmentName(att)}
                      </a>
                      <button
                        type="button"
                        onClick={() => deleteAttachment(att.id)}
                        disabled={busy}
                        className="shrink-0 text-xs px-2 py-1 rounded-lg bg-white hover:bg-gray-50 text-red-700 ring-1 ring-red-200 disabled:opacity-50"
                        title="Delete attachment"
                      >
                        Delete
                      </button>
                    </div>
                  );
                })()}

                <button
                  onClick={() => openExtractModal(item)}
                  disabled={busy || !hasAttachment(item, "agreement")}
                  className="mt-3 text-sm px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-gray-900 ring-1 ring-gray-300 disabled:opacity-40"
                  title={!hasAttachment(item, "agreement") ? "Upload agreement first" : ""}
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2"><Spinner /> Working…</span>
                  ) : (
                    "Extract Pages"
                  )}
                </button>
              </div>
            </div>

            <div className="mt-4 rounded-xl bg-white ring-1 ring-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900">Client Upload</div>
              <div className="text-xs text-gray-600 mt-1">
                {hasAttachment(item, "client_signed") ? "Received" : "Not received"}
              </div>

              {(() => {
                const att = getAttachment(item, "client_signed");
                if (!att || !att.file) return null;
                const raw = att.file_url || att.file;
                if (!raw) return null;
                const href = raw.startsWith("http") ? raw : `${API_URL}${raw}`;
                return (
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <a
                        className="text-sm text-indigo-700 hover:text-indigo-900 underline break-all"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View / Download: {attachmentName(att)}
                      </a>
                      <button
                        type="button"
                        onClick={() => deleteAttachment(att.id)}
                        disabled={busy}
                        className="shrink-0 text-xs px-2 py-1 rounded-lg bg-white hover:bg-gray-50 text-red-700 ring-1 ring-red-200 disabled:opacity-50"
                        title="Delete attachment"
                      >
                        Delete
                      </button>
                    </div>
                );
              })()}

              <label className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer disabled:opacity-50">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) uploadClientSigned(item.id, file);
                    e.target.value = "";
                  }}
                  disabled={busy}
                />
                {busy ? (
                  <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Uploading…</span>
                ) : (
                  "Upload Client Copy"
                )}
              </label>

              <div className="mt-2 text-xs text-gray-600 break-all">
                Upload link (for reference):{" "}
                <span className="text-indigo-700">
                  {`${window.location.origin}/client/reaffirm/${item.client_upload_token}`}
                </span>
              </div>
                          <div className="mt-4 rounded-xl bg-gray-50 ring-1 ring-gray-200 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-gray-900">Review client upload</div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-white text-gray-700 ring-1 ring-gray-300">
                  {hasAttachment(item, "client_signed") ? "Client upload received" : "No client upload yet"}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  onClick={() => acceptItem(item.id)}
                  disabled={busy || !hasAttachment(item, "client_signed")}
                  className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-white text-sm disabled:opacity-50"
                  title={!hasAttachment(item, "client_signed") ? "No client upload to accept yet" : "Accept the client upload"}
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Working…</span>
                  ) : (
                    "Accept Client Upload"
                  )}
                </button>
                <button
                  onClick={() => openRejectModal(item)}
                  disabled={busy || !hasAttachment(item, "client_signed")}
                  className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white text-sm disabled:opacity-50"
                  title={!hasAttachment(item, "client_signed") ? "No client upload to reject yet" : "Reject the client upload with a reason"}
                >
                  {busy ? (
                    <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Working…</span>
                  ) : (
                    "Reject Client Upload"
                  )}
                </button>

                <button
                  onClick={loadItems}
                  disabled={busy}
                  className="ml-auto px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-gray-900 text-sm ring-1 ring-gray-300 disabled:opacity-50"
                >
                  Refresh
                </button>
              </div>
            </div>
            </div>

            <div className="mt-4 rounded-xl bg-white ring-1 ring-gray-200 p-4">
              <div className="text-sm font-semibold text-gray-900">Final signed reaffirmation</div>
              <div className="text-xs text-gray-600 mt-1">
                {hasAttachment(item, "final_signed") ? "Uploaded" : "Not uploaded"}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                Upload the fully signed reaffirmation agreement (final version).
              </div>

              {(() => {
                const att = getAttachment(item, "final_signed");
                if (!att || !att.file) return null;
                const raw = att.file_url || att.signed_url || att.file;
                if (!raw) return null;
                const href = raw.startsWith("http") ? raw : `${API_URL}${raw}`;
                return (
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <a
                      className="text-sm text-indigo-700 hover:text-indigo-900 underline break-all"
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                    >
                      View: {finalReaffirmationDownloadName(item, att)}
                    </a>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => downloadFinalReaffirmation(item, att)}
                        disabled={busy}
                        className="text-xs px-2 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
                      >
                        Download
                      </button>
                      <button
                        type="button"
                        onClick={() => deleteAttachment(att.id)}
                        disabled={busy}
                        className="text-xs px-2 py-1 rounded-lg bg-white hover:bg-gray-50 text-red-700 ring-1 ring-red-200 disabled:opacity-50"
                        title="Delete attachment"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })()}

              <label className="mt-3 inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer disabled:opacity-50">
                <input
                  type="file"
                  accept="application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadFinalSigned(item.id, f);
                    e.target.value = "";
                  }}
                  disabled={busy}
                />
                {busy ? (
                  <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Uploading…</span>
                ) : (
                  "Upload Final PDF"
                )}
              </label>
            </div>

          </div>
        ))}

        {!sortedItems.length && (
          <div className="rounded-2xl bg-white ring-1 ring-gray-200 p-6 text-gray-700">
            No reaffirmations yet. Click <span className="text-indigo-700">Initialize</span> to create items from secured creditors marked “reaffirm”.
          </div>
        )}
      </div>

      {/* Extract modal */}
      <Modal
        open={extractOpen}
        title={`Extract signature pages${extractItem ? ` — ${extractItem.creditor_name}` : ""}`}
        onClose={() => setExtractOpen(false)}
      >
        <div className="text-sm text-gray-700">
          Enter page numbers or ranges (1-based). Examples: <span className="text-indigo-700">7-8, 10, 12-13</span>
        </div>
        <input
          value={pageText}
          onChange={(e) => setPageText(e.target.value)}
          className="mt-3 w-full rounded-lg bg-white text-gray-900 px-3 py-2 ring-1 ring-gray-300 focus:outline-none focus:ring-indigo-500/40"
          placeholder="e.g., 7-8, 10, 12"
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={() => setExtractOpen(false)}
            className="px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-gray-900 ring-1 ring-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={extractPages}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Working…</span>
            ) : (
              "Extract"
            )}
          </button>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal
        open={rejectOpen}
        title={`Reject reaffirmation${rejectItem ? ` — ${rejectItem.creditor_name}` : ""}`}
        onClose={() => setRejectOpen(false)}
      >
        <div className="text-sm text-gray-700">
          Add a short reason (client will see this and get a re-upload link).
        </div>
        <textarea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          className="mt-3 w-full min-h-[110px] rounded-lg bg-white text-gray-900 px-3 py-2 ring-1 ring-gray-300 focus:outline-none focus:ring-red-500/40"
          placeholder="e.g., Missing Debtor 2 signature on page 8, please re-sign and upload again."
        />
        <div className="mt-4 flex gap-2 justify-end">
          <button
            onClick={() => setRejectOpen(false)}
            className="px-3 py-2 rounded-lg bg-white hover:bg-gray-50 text-gray-900 ring-1 ring-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={rejectItemSubmit}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-50"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2"><Spinner className="border-t-white" /> Working…</span>
            ) : (
              "Reject"
            )}
          </button>
        </div>
      </Modal>
    </div>
  );
}
